import { memo } from "react";
import * as Styled from "./style";
import { usePreview } from "./use-preview";
import { useRouter } from "next/router";
import { ItemEpisode } from "~/components/blocks/product/common/blocks/item-episode";
import { ItemEpisodeDetailSkeleton } from "~/components/blocks/product/common/blocks/item-episode/detail-skeleton";
import { TagList } from "~/components/blocks/product/common/blocks/tag-list";
import { PATHS } from "~/const/paths";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import { useQueryString } from "~/hooks/use-query-string";
import { durationTimeText } from "~/util/duration-time-text";

/** 20話以上予告編がなかったら`CarouselTag`を表示しない */
const MAX_TOTAL_PREVIEW_LENGTH = 20;

type PreviewProps = {
  /** 予告編映像の合計数 */
  totalPreviewLength: number;
};

/** 予告編映像タブクリック時に表示するコンポーネント */
export const Preview = memo((props: PreviewProps) => {
  const { totalPreviewLength } = props;

  const { push } = useRouter();
  const { season } = useQueryString();

  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "LANDSCAPE" });

  const {
    activeIndex,
    itemHeight,
    imageWidth,
    imageHeight,
    previewTagListArray,
    isVideoPvEpisodeLoading,
    pvEpisodeEdges,
  } = usePreview({ totalPreviewLength });

  return (
    <Styled.DivPreviewRoot existPvEpisodeTab={totalPreviewLength > MAX_TOTAL_PREVIEW_LENGTH}>
      {totalPreviewLength > MAX_TOTAL_PREVIEW_LENGTH && (
        <Styled.DivTagListCarouselBox>
          <TagList activeIndex={activeIndex} tagListArray={previewTagListArray} />
        </Styled.DivTagListCarouselBox>
      )}

      {!isVideoPvEpisodeLoading && pvEpisodeEdges !== undefined ? (
        <>
          {pvEpisodeEdges.map(({ node }, idx) => {
            return (
              <ItemEpisode
                key={`pv-episode-${idx}`}
                {...imageSrcFormat({ imgSrc: node.episodeImage })}
                alt={node.episodeTitle}
                episodeImageWidth={imageWidth}
                episodeImageHeight={imageHeight}
                episodeTitle={node.episodeTitle}
                episodeDescription={node.episodeDetail}
                visiblePlayIcon // MEMO（kimura）: 視聴権が誰でもあるので、常時表示状態でOK
                imgSupplementText={durationTimeText(node.playInfo.duration)}
                aspectRatio="9/16"
                onThumbnailClick={async () => {
                  // MEMO（kimura）: 押下時に、予告編を再生する（プレイヤー画面に遷移する）
                  // MEMO（kimura）: 視聴権の有無によらず、未ログイン状態でも視聴可能
                  void push(
                    {
                      pathname: PATHS.VOD_PLAYER,
                      query: {
                        season,
                        content: node.id,
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    }
                  );
                }}
              />
            );
          })}
        </>
      ) : (
        <ItemEpisodeDetailSkeleton
          skeletonLength={20}
          oneItemWidthRatio={100}
          itemHeight={itemHeight}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        />
      )}
    </Styled.DivPreviewRoot>
  );
});

Preview.displayName = "Preview";
